export const QUERY_KEYS = {
  auth: {
    profile: () => ['auth', 'profile'] as const,
  },
  branding: {
    config: () => ['branding', 'config'] as const,
  },
  policies: {
    list:             (params: unknown) => ['policies', 'list', params] as const,
    detail:           (id: string) => ['policies', 'detail', id] as const,
    queue:            (params: unknown) => ['policies', 'queue', params] as const,
    endorsements:     (policyId: string) => ['policies', 'endorsements', policyId] as const,
    renewalsDue:      (params: unknown) => ['policies', 'renewals-due', params] as const,
    calculatePremium: (params: unknown) => ['policies', 'calculate-premium', params] as const,
    appChannelList:   (params: unknown) => ['policies', 'app-channel', 'list', params] as const,
    appChannelDetail: (id: string)      => ['policies', 'app-channel', 'detail', id] as const,
  },
  claims: {
    list:                 (params: unknown) => ['claims', 'list', params] as const,
    detail:               (id: string) => ['claims', 'detail', id] as const,
    recourseList:         (params: unknown) => ['claims', 'recourse', 'list', params] as const,
    recourseDetail:       (id: string) => ['claims', 'recourse', 'detail', id] as const,
    litigationList:       (params: unknown) => ['claims', 'litigation', 'list', params] as const,
    litigationDetail:     (id: string) => ['claims', 'litigation', 'detail', id] as const,
    reservesSummary:      () => ['claims', 'reserves-summary'] as const,
    fraudFlagged:         (params: unknown) => ['claims', 'fraud-flagged', params] as const,
    workload:             () => ['claims', 'workload'] as const,
    appChannelList:       (params: unknown) => ['claims', 'app-channel', 'list', params] as const,
    appChannelDetail:     (id: string) => ['claims', 'app-channel', 'detail', id] as const,
    fraudRules:           () => ['claims', 'fraud-rules'] as const,
    blacklist:            (params: unknown) => ['claims', 'blacklist', params] as const,
  },
  clients: {
    list:           (params: unknown) => ['clients', 'list', params] as const,
    detail:         (id: string) => ['clients', 'detail', id] as const,
    policies:       (id: string, params: unknown) => ['clients', id, 'policies', params] as const,
    claims:         (id: string, params: unknown) => ['clients', id, 'claims', params] as const,
    payments:       (id: string, params: unknown) => ['clients', id, 'payments', params] as const,
    communications: (id: string) => ['clients', id, 'communications'] as const,
  },
  agents: {
    list:        (params: unknown) => ['agents', 'list', params] as const,
    deletedList: (params: unknown) => ['agents', 'deleted', params] as const,
    detail:      (id: string) => ['agents', 'detail', id] as const,
    policies:    (id: string, params: unknown) => ['agents', id, 'policies', params] as const,
    commissions: (id: string, params: unknown) => ['agents', id, 'commissions', params] as const,
    messages:    (id: string, params: unknown) => ['agents', id, 'messages', params] as const,
    messageDetail: (agentId: string, msgId: string) => ['agents', agentId, 'messages', msgId] as const,
  },
  broker: {
    dashboard:     () => ['broker', 'dashboard'] as const,
    policies:      (params: unknown) => ['broker', 'policies', params] as const,
    policyDetail:  (id: string) => ['broker', 'policies', 'detail', id] as const,
    commissions:   (params: unknown) => ['broker', 'commissions', params] as const,
    documents:     (params: unknown) => ['broker', 'documents', params] as const,
    messages:      (params: unknown) => ['broker', 'messages', params] as const,
    messageDetail: (id: string) => ['broker', 'messages', 'detail', id] as const,
  },
  billing: {
    overview:           () => ['billing', 'overview'] as const,
    premiums:           (params: unknown) => ['billing', 'premiums', params] as const,
    premiumsOverdue:    (params: unknown) => ['billing', 'premiums', 'overdue', params] as const,
    premiumsDashboard:  () => ['billing', 'premiums', 'dashboard'] as const,
    commissions:        (params: unknown) => ['billing', 'commissions', params] as const,
    commissionsSummary: () => ['billing', 'commissions', 'summary'] as const,
    claimPayouts:       (params: unknown) => ['billing', 'claim-payouts', params] as const,
    treaties:           (params: unknown) => ['billing', 'treaties', params] as const,
    ceded:              (params: unknown) => ['billing', 'ceded', params] as const,
    bordereau:          (period: string) => ['billing', 'bordereau', period] as const,
    tax:                (params: unknown) => ['billing', 'tax', params] as const,
    taxSummary:         (period: string) => ['billing', 'tax', 'summary', period] as const,
    ledger:             (params: unknown) => ['billing', 'ledger', params] as const,
    ledgerSummary:      (params?: unknown) => ['billing', 'ledger', 'summary', params] as const,
    trialBalance:       () => ['billing', 'ledger', 'trial-balance'] as const,
    mobileLedger:       (params: unknown) => ['billing', 'mobile-ledger', params] as const,
    mobileLedgerSummary: (params?: unknown) => ['billing', 'mobile-ledger', 'summary', params] as const,
    reports: {
      gwp:            (params: unknown) => ['billing', 'reports', 'gwp', params] as const,
      lossRatio:      (params: unknown) => ['billing', 'reports', 'loss-ratio', params] as const,
      combinedRatio:  (params: unknown) => ['billing', 'reports', 'combined-ratio', params] as const,
      pl:             (params: unknown) => ['billing', 'reports', 'pl', params] as const,
      revenueAccount: (params: unknown) => ['billing', 'reports', 'revenue-account', params] as const,
    },
    aging:              () => ['billing', 'aging'] as const,
    uprSummary:         () => ['billing', 'upr', 'summary'] as const,
    uprRecords:         (params: unknown) => ['billing', 'upr', 'records', params] as const,
    reserves:           (params: unknown) => ['billing', 'reserves', params] as const,
    reservesSummary:    (quarter?: string) => ['billing', 'reserves', 'summary', quarter] as const,
    solvencyLatest:     () => ['billing', 'solvency', 'latest'] as const,
    solvencyRecords:    (params: unknown) => ['billing', 'solvency', 'records', params] as const,
    periods:            () => ['billing', 'periods'] as const,
    remittances:        (params: unknown) => ['billing', 'remittances', params] as const,
    remittanceSummary:  (period?: string) => ['billing', 'remittances', 'summary', period] as const,
  },
  analytics: {
    overview:         (params: unknown) => ['analytics', 'overview', params] as const,
    underwriting:     (params: unknown) => ['analytics', 'underwriting', params] as const,
    claims:           (params: unknown) => ['analytics', 'claims', params] as const,
    financial:        (params: unknown) => ['analytics', 'financial', params] as const,
    agentPerformance: (params: unknown) => ['analytics', 'agent-performance', params] as const,
    capitalAdequacy: {
      summary:      (params: unknown) => ['analytics', 'capital-adequacy', 'summary', params] as const,
      reserve:      (params: unknown) => ['analytics', 'capital-adequacy', 'reserve', params] as const,
      upr:          (params: unknown) => ['analytics', 'capital-adequacy', 'upr', params] as const,
      lossRatio:    (params: unknown) => ['analytics', 'capital-adequacy', 'loss-ratio', params] as const,
      reinsurance:  (params: unknown) => ['analytics', 'capital-adequacy', 'reinsurance', params] as const,
      debtorAgeing: (params: unknown) => ['analytics', 'capital-adequacy', 'debtor-ageing', params] as const,
      headroom:     (params: unknown) => ['analytics', 'capital-adequacy', 'headroom', params] as const,
      inputCurrent: () => ['analytics', 'capital-adequacy', 'input', 'current'] as const,
      inputHistory: () => ['analytics', 'capital-adequacy', 'input', 'history'] as const,
    },
  },
  hr: {
    overview:       () => ['hr', 'overview'] as const,
    employees:      (params: unknown) => ['hr', 'employees', params] as const,
    employeeDetail: (id: string) => ['hr', 'employees', 'detail', id] as const,
    salary:         (id: string) => ['hr', 'employees', 'salary', id] as const,
    kpis:           (id: string) => ['hr', 'employees', 'kpis', id] as const,
    orgChart:       () => ['hr', 'org-chart'] as const,
    config:         () => ['hr', 'config'] as const,
    leave:          (params: unknown) => ['hr', 'leave', params] as const,
    leaveDetail:    (id: string) => ['hr', 'leave', 'detail', id] as const,
    departments:    () => ['hr', 'departments'] as const,
    activity:       (entityType: string, entityId: string) => ['audit', 'entity', entityType, entityId] as const,
  },
  products: {
    list:   (params: unknown) => ['products', 'list', params] as const,
    detail: (id: string) => ['products', 'detail', id] as const,
    active: () => ['products', 'active'] as const,
  },
  stp: {
    rules:     (params: unknown) => ['stp', 'rules', params] as const,
    analytics: (params: unknown) => ['stp', 'analytics', params] as const,
  },
  notifications: {
    list:        () => ['notifications', 'list'] as const,
  },
  subscription: {
    status: () => ['subscription', 'status'] as const,
    seats: () => ['subscription', 'seats'] as const,
  },
  platform: {
    dashboard:        () => ['platform', 'dashboard'] as const,
    overview:         () => ['platform', 'overview'] as const,
    clients:          (params: unknown) => ['platform', 'clients', params] as const,
    clientDetail:     (id: string) => ['platform', 'clients', 'detail', id] as const,
    subscriptions:    (params: unknown) => ['platform', 'subscriptions', params] as const,
    trials:           () => ['platform', 'trials'] as const,
    invoices:         (params: unknown) => ['platform', 'invoices', params] as const,
    invoiceSummary:   () => ['platform', 'invoices', 'summary'] as const,
    revenue:          (period: unknown) => ['platform', 'revenue', period] as const,
    modules:          () => ['platform', 'modules', 'adoption'] as const,
    audit:            (params: unknown) => ['platform', 'audit', params] as const,
    companyAuditLogs: (id: string, params: unknown) => ['platform', 'clients', id, 'audit-logs', params] as const,
    activity:         (params: unknown) => ['platform', 'activity', params] as const,
    activitySummary:  (params: unknown) => ['platform', 'activity', 'summary', params] as const,
    companyActivity:  (id: string, params: unknown) => ['platform', 'activity', id, params] as const,
    settings:         () => ['platform', 'settings'] as const,
    admins:           () => ['platform', 'admins'] as const,
  },
  settings: {
    users:        (params: unknown) => ['settings', 'users', params] as const,
    deletedUsers: (params: unknown) => ['settings', 'deleted-users', params] as const,
    roles:        () => ['settings', 'roles'] as const,
    webhooks: () => ['settings', 'webhooks'] as const,
    apiKeys:  () => ['settings', 'api-keys'] as const,
    auditLog: (params: unknown) => ['settings', 'audit-log', params] as const,
    company:  () => ['settings', 'company'] as const,
  },
  rbac: {
    myPermissions:       () => ['rbac', 'me'] as const,
    allPermissions:      () => ['rbac', 'permissions'] as const,
    rolePermissions:     (roleId: string) => ['rbac', 'role', roleId, 'permissions'] as const,
    userPermissions:     (userId: string) => ['rbac', 'user', userId, 'permissions'] as const,
  },
  audit: {
    entity: (entityType: string, entityId: string) =>
      ['audit', 'entity', entityType, entityId] as const,
  },
  onboarding: {
    status: () => ['onboarding', 'status'] as const,
  },
  integration: {
    webhooks: () => ['integration', 'webhooks'] as const,
    apiKeys: () => ['integration', 'api-keys'] as const,
  },
  migration: {
    jobs: (params: unknown) => ['migration', 'jobs', params] as const,
    job: (id: string) => ['migration', 'job', id] as const,
  },
  inspections: {
    list:     (params: unknown) => ['inspections', 'list', params] as const,
    detail:   (id: string) => ['inspections', 'detail', id] as const,
    damageSvg:(id: string) => ['inspections', 'damage-svg', id] as const,
    workflows:() => ['inspections', 'workflows'] as const,
    usage:    () => ['inspections', 'usage'] as const,
  },
  certificates: {
    templateConfig: (productLine: string) => ['certificates', 'template-config', productLine] as const,
    templatePreview: (productLine: string) => ['certificates', 'template-preview', productLine] as const,
    certificate: (policyId: string) => ['certificates', 'certificate', policyId] as const,
    preview: (policyId: string) => ['certificates', 'preview', policyId] as const,
  },
  wording: {
    templateConfig:  (productLine: string, country: string) => ['wording', 'template-config', productLine, country] as const,
    templatePreview: (productLine: string, country: string) => ['wording', 'template-preview', productLine, country] as const,
    sections:        (productLine: string, country: string) => ['wording', 'sections', productLine, country] as const,
    clauseLibrary:   (productLine: string, country: string) => ['wording', 'clause-library', productLine, country] as const,
    wording:         (policyId: string) => ['wording', 'wording', policyId] as const,
    preview:         (policyId: string) => ['wording', 'preview', policyId] as const,
    versions:        (policyId: string) => ['wording', 'versions', policyId] as const,
  },
};
